import { type Rive } from '@rive-app/react-canvas';
import { useEffect, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

export type UseStateMachineDetectionParams = {
  rive: Rive | null;
  errorContext?: string;
};

export type UseStateMachineDetectionResult = {
  detectedStateMachines: string[];
  detectionComplete: boolean;
};

/**
 * Determines if a file is a Rive animation file
 */
export const isRiveFile = (clip: { ext: string }): boolean => {
  return clip.ext.toLowerCase() === 'riv';
};

/**
 * Determines if a file extension indicates a Rive file
 */

/**
 * Hook for detecting Rive state machines from a loaded Rive instance.
 * This hook only handles state machine detection logic.
 */
export const useStateMachineDetection = ({
  rive,
  errorContext = 'Failed to detect Rive state machines',
}: UseStateMachineDetectionParams): UseStateMachineDetectionResult => {
  const [detectedStateMachines, setDetectedStateMachines] = useState<string[]>([]);
  const [detectionComplete, setDetectionComplete] = useState(false);

  // Detect all state machines using the public API
  useEffect(() => {
    if (rive && !detectionComplete) {
      try {
        const stateMachineNames = rive.stateMachineNames;

        // Set all detected state machines
        setDetectedStateMachines(stateMachineNames);
        setDetectionComplete(true);
      } catch (error) {
        reportErrorToBugsnag({
          error,
          context: errorContext,
          metadata: { stateMachineNames: rive.stateMachineNames },
        });
        setDetectedStateMachines([]);
        setDetectionComplete(true);
      }
    }
  }, [rive, detectionComplete, errorContext]);

  return {
    detectedStateMachines,
    detectionComplete,
  };
};
